import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Coins,
  Headset,
  Landmark,
  MapPin,
  MonitorSmartphone,
  ReceiptText,
  ChevronRight,
} from "lucide-react";

import mapTotal from "../assets/map-total.png";
import mapSeoul from "../assets/map-seoul.png";
import mapBusan from "../assets/map-busan.png";
import mapDaejeon from "../assets/map-daejeon.png";
import mapGyeonggi from "../assets/map-gyeonggi.png";
import mapIncheon from "../assets/map-incheon.png";
import mapJeonbuk from "../assets/map-jeonbuk.png";
import mapGwangju from "../assets/map-gwangju.png";
import landingVideo from "../assets/goodrich-landing.mp4";

const REGIONS = [
  "전체",
  "서울",
  "부산",
  "대전",
  "경기",
  "인천",
  "전북",
  "광주",
];

const BRANCHES = [
  {
    name: "굿리치 플러스 사업부 본사",
    region: "서울",
    address: "서울 중구 퇴계로36가길 10",
  },
  {
    name: "굿리치 플러스 사업부 지부",
    region: "서울",
    address: "서울 중구 필동로47 주성빌딩",
  },
  {
    name: "골드센터",
    region: "서울",
    address: "서울 중구 쌍림동 146-7번지 한림빌딩",
  },
  {
    name: "리치앤멀티플",
    region: "서울",
    address: "서울 금천구 가산디지털1로 205-27",
  },
  {
    name: "리치앤TOP",
    region: "서울",
    address: "서울 송파구 법원로 128, B동",
  },
  {
    name: "굿드림",
    region: "서울",
    address: "서울시 중구 필동로 37 평강빌딩 3층",
  },
  {
    name: "리치앤지앤지",
    region: "부산",
    address: "부산광역시 연제구 신촌로21",
  },
  {
    name: "리치앤파트너스",
    region: "부산",
    address: "부산광역시 연제구 법원로32번길 19",
  },
  {
    name: "KNTRC 안산",
    region: "경기",
    address: "경기도 안산시 상록구 안산천남1로 140",
  },
  {
    name: "리치앤위드",
    region: "전북",
    address: "전북 전주시 완산구 홍산로 261",
  },
  {
    name: "안성지점",
    region: "경기",
    address: "경기도 안성시 아양1로 77-31",
  },
  {
    name: "대전지점",
    region: "대전",
    address: "대전광역시 서구 도안대로 55",
  },
  {
    name: "링크플러스지점",
    region: "대전",
    address: "대전광역시 동구 계족로 496",
  },
  {
    name: "KNTRC 경기",
    region: "경기",
    address: "경기 수원시 영통구 대학로 12",
  },
  {
    name: "KNTRC 서울",
    region: "경기",
    address: "경기 구리시 건원대로 3",
  },
  {
    name: "KNTRC 인천",
    region: "인천",
    address: "인천 수구 원당대로 664",
  },
  {
    name: "KNTRC 광주",
    region: "광주",
    address: "광주광역시 광산구 장덕동 1628",
  },
];

const STATS = [
  { value: 700, suffix: "만명", label: "굿리치 앱 다운로드" },
  { value: 150, suffix: "만건", label: "보험금 청구처리" },
  { value: 150, suffix: "%", label: "설계사 인당 생산성", up: true },
  { value: 17, suffix: "개", label: "전국 지점 안내" },
];

const SERVICES = [
  { key: "finance", icon: Landmark, label: "금융서비스" },
  { key: "products", icon: ReceiptText, label: "보험상품" },
  { key: "diagnosis", icon: Coins, label: "퇴직연금 서비스" },
  { key: "platform", icon: Headset, label: "설계사플랫폼" },
  { key: "planner", icon: MonitorSmartphone, label: "리치플래너" },
  { key: "event", icon: MapPin, label: "현장행사" },
];

const MAP_IMAGES = {
  전체: mapTotal,
  서울: mapSeoul,
  부산: mapBusan,
  대전: mapDaejeon,
  경기: mapGyeonggi,
  인천: mapIncheon,
  전북: mapJeonbuk,
  광주: mapGwangju,
};

const popupPath = "/chatbot";
const features = {
  width: 420,
  height: 780,
};

function CountUp({ end, suffix = "", duration = 1600 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  const [count, setCount] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    let rafId;
    const startTime = performance.now();

    const tick = (now) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);

      setProgress(eased);
      setCount(Math.floor(end * eased));

      if (p < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setCount(end);
        setProgress(1);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isInView, end, duration]);

  return (
    <span
      ref={ref}
      style={{
        opacity: 0.5 + progress * 0.5,
        transform: `translateY(${(1 - progress) * 4}px) scale(${0.96 + progress * 0.04})`,
        transition: "opacity 0.1s linear, transform 0.1s linear",
        display: "inline-block",
      }}
      className="tabular-nums"
    >
      {count}
      {suffix}
    </span>
  );
}

function ServiceIcon({
  icon: Icon,
  label,
  delay = 0,
  onClick,
  disabled = false,
}) {
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      whileHover={disabled ? undefined : { y: -4, scale: 1.02 }}
      whileTap={disabled ? undefined : { scale: 0.98 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay }}
      onClick={onClick}
      className={[
        "flex flex-col items-center text-center",
        disabled ? "cursor-default" : "cursor-pointer",
      ].join(" ")}
    >
      <div className="flex h-[88px] w-[88px] items-center justify-center sm:h-[100px] sm:w-[100px] md:h-[120px] md:w-[120px]">
        <div className="relative flex h-[72px] w-[72px] items-center justify-center rounded-[22px] border-[2.5px] border-secondary bg-transparent sm:h-[82px] sm:w-[82px] md:h-[96px] md:w-[96px] md:rounded-[28px] md:border-[3px]">
          <Icon
            className="h-8 w-8 text-secondary sm:h-9 sm:w-9 md:h-12 md:w-12"
            strokeWidth={2.2}
          />
          <span className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full border-[2.5px] border-main bg-white sm:h-7 sm:w-7 md:-right-2 md:-top-2 md:h-8 md:w-8 md:border-[3px]">
            <span className="h-2 w-2 rounded-full bg-main md:h-2.5 md:w-2.5" />
          </span>
        </div>
      </div>

      <p className="mt-3 text-sm font-semibold tracking-[-0.02em] text-gray-700 sm:text-base md:text-[17px]">
        {label}
      </p>
    </motion.button>
  );
}

function StatChartBg() {
  return (
    <svg
      viewBox="0 0 1200 260"
      className="absolute inset-x-0 bottom-0 h-[140px] w-full opacity-60 sm:h-[160px] md:h-[180px]"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <g stroke="#dcdcdc" strokeWidth="1.2">
        {[...Array(28)].map((_, i) => {
          const x = 20 + i * 42;
          const h = 28 + ((i * 31) % 110);
          return (
            <g key={i}>
              <line x1={x} y1="250" x2={x} y2={40} />
              <rect
                x={x - 4}
                y={250 - h}
                width="8"
                height={h}
                fill="#e4e4e4"
                stroke="none"
              />
            </g>
          );
        })}
      </g>

      <path
        d="M0 220 C80 205, 120 160, 180 170 S300 240, 370 200 S470 120, 560 145 S660 235, 760 180 S910 90, 1040 150 S1130 220, 1200 120"
        fill="none"
        stroke="#d6d6d6"
        strokeWidth="2"
      />
    </svg>
  );
}

export default function MainLandingPage() {
  const navigate = useNavigate();
  const branchSectionRef = useRef(null);
  const [activeRegion, setActiveRegion] = useState("전체");

  const filteredBranches = useMemo(() => {
    if (activeRegion === "전체") return BRANCHES;
    return BRANCHES.filter((item) => item.region === activeRegion);
  }, [activeRegion]);

  const currentMapImage = MAP_IMAGES[activeRegion] || mapTotal;

  const openPopup = () => {
    const w = features.width;
    const h = features.height;

    const dualScreenLeft =
      window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop =
      window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth;
    const height = window.innerHeight;

    const left = width / 2 - w / 2 + dualScreenLeft;
    const top = height / 2 - h / 2 + dualScreenTop;

    const opts = `
      width=${w},
      height=${h},
      left=${left},
      top=${top},
      resizable=yes,
      scrollbars=yes
    `;

    window.open(popupPath, "chatbot", opts);
  };

  const handleServiceClick = (key) => {
    switch (key) {
      case "finance":
        navigate("/finance-services");
        break;

      case "products":
        navigate("/products");
        break;

      case "diagnosis":
        navigate("/diagnosis");
        break;

      case "platform":
        navigate("/platform");
        break;

      case "planner":
        openPopup();
        break;

      case "event":
        window.open(
          "https://youtu.be/1XX2igwOLIw?si=cfo4e5X4VcBzjLKw",
          "_blank",
        );
        break;

      default:
        break;
    }
  };

  return (
    <main className="bg-white">
      <section className="relative h-screen overflow-hidden">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
        >
          <source src={landingVideo} type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-black/45" />

        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] items-center px-5 sm:px-8 md:px-12 xl:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-[980px]"
          >
            <p className="mb-4 inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[11px] font-bold tracking-[0.14em] text-white backdrop-blur sm:mb-6 sm:px-5 sm:text-sm sm:tracking-[0.16em]">
              GOODRICH PLUS BUSINESS UNIT
            </p>

            <h1 className="text-[22px] font-extrabold leading-[1.4] text-white sm:text-[28px] md:text-[34px] lg:text-[38px] xl:text-[42px]">
              24년 경험을 바탕으로
              <br />
              고객의 신뢰를 쌓아온 태아보험넷, 이제
              <br />
              <span className="text-main text-[26px] sm:text-[34px] md:text-[42px] lg:text-[46px] xl:text-[52px]">
                굿리치 플러스사업부
              </span>
              로 더 크게 도약합니다.
            </h1>

            <p className="mt-5 max-w-[760px] text-base leading-[1.8] tracking-[-0.02em] text-white/85 sm:mt-6 sm:text-lg md:mt-8 md:text-xl">
              데이터와 현장 경험을 기반으로 보험 컨설팅과 보험 상품 안내를
              연결하고, 고객에게는 더 명확한 선택을, 플래너에게는 더 안정적인
              성장을 제공합니다.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 pb-20 pt-12 sm:px-8 sm:pb-24 sm:pt-16 md:px-12 xl:px-16">
        <div className="pt-8 sm:pt-12 md:pt-20 lg:pt-28">
          <h2 className="text-[30px] font-extrabold leading-[1.3] text-secondary sm:text-[38px] md:text-[44px] lg:text-5xl">
            전문 플래너가 만드는 실용 보험 케어,
            <span className="text-main"> 굿리치 플러스사업부</span>
          </h2>
          <p className="mt-4 text-base text-gray-700 sm:mt-6 sm:text-lg md:mt-8 md:text-xl">
            고객 상황에 맞춘 분석·보장·관리까지, 실전 컨설팅을 제공합니다.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-8 pt-10 sm:gap-x-6 sm:gap-y-10 sm:pt-12 md:grid-cols-3 lg:grid-cols-6 lg:pt-14">
          {SERVICES.map((item, idx) => (
            <ServiceIcon
              key={item.key}
              icon={item.icon}
              label={item.label}
              delay={idx * 0.05}
              onClick={() => handleServiceClick(item.key)}
            />
          ))}
        </div>

        <div className="pt-20 sm:pt-28 md:pt-32 lg:pt-40">
          <h2 className="text-[30px] font-extrabold leading-[1.3] text-secondary sm:text-[38px] md:text-[44px] lg:text-5xl">
            <span className="mb-2 block text-[24px] text-main sm:text-[28px] md:text-[32px] lg:text-4xl">
              24년 고객관리 기반
            </span>
            실무 중심 디지털 시스템을 제공합니다.
          </h2>

          <div className="relative mt-8 overflow-hidden rounded-[24px] bg-white px-5 py-10 shadow-sm sm:mt-10 sm:px-8 sm:py-12 md:mt-12 md:px-12 md:py-14 lg:px-16 lg:py-20">
            <StatChartBg />

            <div className="relative z-10 grid grid-cols-2 gap-y-10 sm:gap-y-12 lg:grid-cols-4 lg:gap-y-14">
              {STATS.map((item, idx) => (
                <div
                  key={item.label}
                  className={[
                    "relative flex flex-col items-center justify-center px-2 text-center sm:px-4",
                    idx !== STATS.length - 1
                      ? "lg:border-r lg:border-[#dddddd]"
                      : "",
                  ].join(" ")}
                >
                  <div className="text-[34px] font-extrabold leading-none tracking-[-0.05em] text-secondary sm:text-[42px] md:text-[48px] lg:text-5xl">
                    <CountUp end={item.value} suffix={item.suffix} />
                    {item.up ? <span className="ml-1.5 sm:ml-2">↑</span> : null}
                  </div>
                  <p className="mt-2 text-sm font-semibold text-gray-600 sm:mt-3 sm:text-base md:text-lg lg:text-xl">
                    {item.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        ref={branchSectionRef}
        className="mx-auto max-w-[1400px] px-5 pb-20 pt-8 sm:px-8 sm:pb-24 sm:pt-12 md:px-12 md:pt-16 lg:pb-28 lg:pt-24 xl:px-16"
      >
        <div className="grid gap-10 xl:grid-cols-[1.15fr_0.85fr]">
          <div>
            <h2 className="text-[30px] font-extrabold leading-[1.2] text-secondary sm:text-[38px] md:text-[44px] lg:text-5xl">
              전국 지점 안내
            </h2>

            <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-4 sm:mt-10 sm:gap-x-7 sm:gap-y-5 md:mt-12 md:gap-x-8 lg:gap-x-10">
              {REGIONS.map((region, idx) => {
                const active = activeRegion === region;

                return (
                  <button
                    key={region}
                    onClick={() => setActiveRegion(region)}
                    className={[
                      idx === 0 && "group",
                      "inline-flex items-center gap-2 text-base font-bold tracking-[-0.03em] transition sm:text-lg md:text-xl lg:text-[22px]",
                      active
                        ? "text-main"
                        : "text-secondary/80 hover:text-secondary",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {idx === 0 ? (
                      <MapPin
                        className={[
                          "h-5 w-5 transition sm:h-5 sm:w-5 md:h-6 md:w-6",
                          active
                            ? "text-main"
                            : "text-secondary/80 group-hover:text-secondary",
                        ].join(" ")}
                        strokeWidth={2.6}
                      />
                    ) : null}

                    {region}
                  </button>
                );
              })}
            </div>

            <div className="mt-10 grid gap-x-6 gap-y-5 sm:mt-12 sm:gap-y-6 lg:mt-14 lg:grid-cols-2 lg:gap-x-8 lg:gap-y-7">
              {filteredBranches.map((branch) => (
                <div key={branch.name}>
                  <p className="text-[15px] leading-[1.75] text-gray-700 sm:text-base md:text-[17px] lg:text-lg">
                    <span className="font-bold">{branch.name}</span>:{" "}
                    {branch.address}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-start justify-center xl:justify-end">
            <div className="w-full max-w-[500px] pt-2 sm:max-w-[600px] md:max-w-[700px] lg:max-w-[800px] xl:max-w-[900px] xl:pt-20 pl-10">
              <img
                src={currentMapImage}
                alt={`${activeRegion} 지점 지도`}
                className="h-auto w-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="rounded-3xl px-5 py-14 text-center sm:px-6 sm:py-16 md:px-8 md:py-20">
        <div className="mx-auto mb-8 h-[2px] w-14 bg-main sm:mb-10 sm:w-16" />

        <p className="text-xs font-semibold tracking-[0.14em] text-main sm:text-sm">
          GOODRICH PLUS STORY
        </p>

        <h3 className="mt-3 mx-auto max-w-[320px] text-[24px] font-extrabold leading-[1.35] text-secondary sm:max-w-none sm:text-[28px] md:text-[32px]">
          태아보험넷의 성장과 발자취를 확인해보세요
        </h3>

        <p className="mt-3 text-sm leading-6 text-gray-600 sm:text-base md:text-lg">
          2001년부터 현재까지 이어온 신뢰와 성과를 한눈에 볼 수 있습니다.
        </p>

        <button
          onClick={() => navigate("/history")}
          className="
      mt-6 inline-flex items-center gap-2
      rounded-full
      bg-gradient-to-r from-[#ffb347] to-[#ff9601]
      px-6 py-3
      text-sm font-bold text-white
      shadow-[0_6px_18px_rgba(255,150,1,0.35)]
      transition-all duration-300
      hover:scale-[1.04] hover:shadow-[0_10px_24px_rgba(255,150,1,0.45)]
      active:scale-95
      sm:px-7 sm:text-base
    "
        >
          히스토리 보기
          <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>
    </main>
  );
}
